import { Edit, TrendingFlatOutlined, TripOriginOutlined } from '@mui/icons-material';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { formatCurrency } from 'helpers';

const useStyles = makeStyles((theme) => ({
  tableCell: {
    border: '1px solid #E0E0E0',
    height: '30px',
    padding: theme.spacing(1.2),
  },
  tableCellLabel: {
    width: '50%',
  },
  tableContainer: {
    color: '#1C2541',
    marginBottom: '50px',
    marginTop: '16px',
  },
  tableHeader: {
    border: '1px solid #E0E0E0',
    background: '#EFEFEF',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  semiBold: {
    fontWeight: 'bold',
  },
  tableCellContent: {
    display: 'flex',
    alignItems: 'center',
    height: '20px',
    width: '100%',
  },
  editContainer: {
    display: 'flex',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
}));

export type AccountSummaryTableRow = {
  label: string;
  currentValue?: null | number;
  startingValue?: null | number;
  editColumnName?:
    | 'proposedDistributionsToBeneficiaries'
    | 'principalDistributionsToBeneficiaries'
    | 'principalNetGainOrLossesFromSales';
};

export type TableComponentProps = {
  rows: AccountSummaryTableRow[];
  onEditButtonClick: (row: AccountSummaryTableRow) => void;
};

const AccountSummaryTableComponent = ({ rows, onEditButtonClick }: TableComponentProps) => {
  const classes = useStyles();

  return (
    <div className={classes.tableContainer}>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell size="small" className={classes.tableHeader}></TableCell>
              <TableCell size="small" className={classes.tableHeader}>
                <Typography variant="body2" className={classes.headerContent}>
                  <TripOriginOutlined /> Current Value
                </Typography>
              </TableCell>
              <TableCell size="small" className={classes.tableHeader}>
                <Typography variant="body2" className={classes.headerContent}>
                  <TrendingFlatOutlined /> Starting Value
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ label, currentValue, startingValue, editColumnName }, i) => (
              <TableRow key={`${label}${i}`}>
                <TableCell
                  className={clsx(classes.tableCell, classes.tableCellLabel, {
                    [classes.semiBold]: label === 'Total',
                  })}
                >
                  {label}
                </TableCell>
                <TableCell
                  className={clsx(classes.tableCell, {
                    [classes.semiBold]: label === 'Total',
                  })}
                >
                  {currentValue && formatCurrency(String(currentValue * 100))}
                </TableCell>
                <TableCell
                  className={clsx(classes.tableCell, {
                    [classes.semiBold]: label === 'Total',
                  })}
                >
                  <div className={classes.tableCellContent}>
                    {startingValue && formatCurrency(String(startingValue * 100))}
                    <div className={classes.editContainer}>
                      {editColumnName && (
                        <IconButton
                          aria-label="edit"
                          onClick={() =>
                            onEditButtonClick({
                              label,
                              currentValue,
                              startingValue,
                              editColumnName,
                            })
                          }
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      )}
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default AccountSummaryTableComponent;
